import path from 'path';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import {
  AddPanelPopover64Icon,
  ElementPopoverIcon as PopoverIcon,
} from '@packages/systems/spring';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {Component, ElementPreset, Plugins} from '@packages/systems/wfdl';
import {
  EElement,
  EEnum,
  EList,
  ERecord,
  EUnion,
} from '@packages/systems/wfdl-ast';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';

import Popover from './Popover';

const AddPanelPopoverIcon = wrapAddPanelIcon(AddPanelPopover64Icon);

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    Popover: PopoverIcon,
    // @ts-expect-error - Spring's wrapped icon props do not match the WFDL icon type.
    AddPanelPopover: AddPanelPopoverIcon,
  },

  atoms: {
    Popover,
  },

  elementPresets: {
    Popover: ElementPreset.create({
      factory: EElement({
        id: 'Popover',
        type: qualify(Plugins.name('Popover'), Component.name('Popover')),
        data: ERecord({
          popover: EUnion({tag: 'auto', value: ERecord({})}),
          tag: EEnum('div'),
          children: EList([]),
        }),
      }),
      icon: {
        small: qualify(Plugins.name('Popover'), iconName('Popover')),
        medium: qualify(Plugins.name('Popover'), iconName('AddPanelPopover')),
      },
      label: 'Popover',
      beta: {
        enabledBasedOnFlags: {
          featureFlagName: FEATURE_FLAGS.BUILDING_BLOCKS_POPOVERS,
        },
      },
      helpData: {
        description: [
          'A popover target is a standalone container for content that a trigger can open.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/popover.1c2bd4d293.mp4',
      },
      category: () =>
        getFeatureFlagOutsideOfReact(FEATURE_FLAGS.BUILDING_BLOCKS_POPOVERS)
          ? 'Advanced'
          : null,
    }),
  },

  // Only bundled onto pages that actually have a popover. The module is inert
  // unless a popover also carries an IX3 open/close animation — it exists to
  // hold an animated popover in the top layer for the length of its close
  // timeline, which the native invoker path cannot do.
  siteModules: [
    {
      // No extension: rspack resolves it against `.ts` then `.js` (the
      // `resolve.extensions` in `perPageModuleConfig`), so it finds whichever
      // exists. A literal `.ts` fails every publish of a page with a popover on
      // deployed servers, which run swc output (`build-packages.sh`) where the
      // module only exists as `webflow-popover.js`. Deriving the extension from
      // `__filename` fails the other way: a server that compiles only the files
      // it imports (the CI backend) loads `index.js` beside a `webflow-popover.ts`
      // nothing compiled, so the `.js` entry does not exist.
      //
      // The `.js` that deployed servers resolve is never run through the rspack
      // browserslist pass (`perPageModuleConfig` only applies it to `.ts`), so
      // deployed servers publish this module with the syntax swc's node 22
      // target left in it. Keep `webflow-popover.ts` to syntax every supported
      // browser runs.
      filePaths: [path.resolve(__dirname, 'webflow-popover')],
      bundleIf: {
        type: 'node',
        test: ({data = {}}) => data.popover !== undefined,
      },
    },
  ],
});
